import { useRef, useState } from "react";
import { FileText, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  useImportFiles,
  usePreviewFromDrive,
  usePreviewFromUpload,
  useCommitImport,
} from "../../hooks/useImportServers";
import { errorMessage } from "../../hooks/useServers";

const STATE_LABEL = {
  nuevo: { text: "Nuevo", variant: "default" },
  revisar: { text: "Revisar", variant: "secondary" },
  ya_existe: { text: "Ya existe", variant: "secondary" },
  incompleto: { text: "Incompleto", variant: "secondary" },
};

function CandidateRow({ candidate, checked, name, onToggle, onName }) {
  const state = STATE_LABEL[candidate.state] || STATE_LABEL.incompleto;
  return (
    <div data-testid={`import-row-${candidate.id}`} className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-1.5 h-4 w-4 accent-emerald-600"
          checked={checked}
          disabled={!candidate.selectable}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={`Importar ${candidate.suggested_name}`}
        />
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              value={name}
              onChange={(e) => onName(e.target.value)}
              disabled={!candidate.selectable}
              className="h-8 w-40"
              aria-label="Nombre del servidor"
            />
            <Badge variant={state.variant} className="text-[10px]">
              {state.text}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground font-mono">
            {candidate.username || "¿usuario?"}@{candidate.host || "¿host?"}:{candidate.port}
          </p>
          <p className="text-xs text-muted-foreground">
            Contraseña: {candidate.secret_detected ? `••• detectada (${candidate.secret_length} caracteres)` : "no detectada"}
            {" · "}Sección: {candidate.section}
          </p>
          {candidate.reasons.length > 0 && (
            <ul className="text-xs text-amber-600 dark:text-amber-500 list-disc pl-4">
              {candidate.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export function ImportServersDialog({ open, onOpenChange, project }) {
  const projectId = project?.id;
  const [step, setStep] = useState("source");
  const [preview, setPreview] = useState(null);
  const [checked, setChecked] = useState({});
  const [names, setNames] = useState({});
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const committing = useRef(false); // anti doble clic (además del candado atómico del servidor)
  const fileInput = useRef(null);

  const { data: files, isLoading } = useImportFiles(projectId, open);
  const fromDrive = usePreviewFromDrive(projectId);
  const fromUpload = usePreviewFromUpload(projectId);
  const commit = useCommitImport(projectId);
  const busy = fromDrive.isPending || fromUpload.isPending;

  const reset = () => {
    setStep("source");
    setPreview(null);
    setChecked({});
    setNames({});
    setResults(null);
    setError("");
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const showPreview = (data) => {
    setPreview(data);
    setChecked(Object.fromEntries(data.candidates.map((c) => [c.id, c.selected_by_default])));
    setNames(Object.fromEntries(data.candidates.map((c) => [c.id, c.suggested_name])));
    setStep("preview");
  };

  const handleDrive = async (path) => {
    if (busy) return;
    setError("");
    try {
      showPreview(await fromDrive.mutateAsync(path));
    } catch (err) {
      setError(errorMessage(err, "No se pudo analizar el archivo."));
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || busy) return;
    setError("");
    try {
      showPreview(await fromUpload.mutateAsync(file));
    } catch (err) {
      setError(errorMessage(err, "No se pudo analizar el archivo."));
    }
  };

  const selectedIds = preview ? preview.candidates.filter((c) => checked[c.id] && c.selectable).map((c) => c.id) : [];

  const handleCommit = async () => {
    if (committing.current || selectedIds.length === 0) return;
    committing.current = true;
    setError("");
    try {
      const data = await commit.mutateAsync({
        sessionId: preview.session_id,
        selections: selectedIds.map((id) => ({ id, name: names[id] })),
      });
      setResults(data.results);
      setStep("done");
    } catch (err) {
      setError(errorMessage(err, "No se pudo importar."));
    } finally {
      committing.current = false;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="import-servers-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar servidores desde un archivo</DialogTitle>
          <DialogDescription>
            El archivo se lee en el servidor, sin IA. Aquí nunca se muestran las contraseñas: solo cuántos caracteres
            se detectaron. Nada se guarda hasta que marques filas y pulses «Importar seleccionados».
          </DialogDescription>
        </DialogHeader>

        {step === "source" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">Archivos de la carpeta de Drive del proyecto</p>
              {isLoading && <p className="text-sm text-muted-foreground">Buscando archivos...</p>}
              {!isLoading && files?.length === 0 && (
                <p className="text-sm text-muted-foreground">No hay archivos .md, .txt o .csv en esa carpeta.</p>
              )}
              <div className="grid gap-1.5">
                {files?.map((f) => (
                  <Button
                    key={f.name}
                    variant="outline"
                    size="sm"
                    className="justify-start"
                    disabled={busy}
                    data-testid={`import-drive-${f.name}`}
                    onClick={() => handleDrive(f.name)}
                  >
                    <FileText className="h-3.5 w-3.5 mr-2 shrink-0" />
                    <span className="truncate">{f.name}</span>
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">O un archivo de tu computadora</p>
              <input ref={fileInput} type="file" accept=".md,.txt,.csv" className="hidden" onChange={handleUpload} data-testid="import-file-input" />
              <Button variant="outline" size="sm" disabled={busy} onClick={() => fileInput.current?.click()}>
                <Upload className="h-3.5 w-3.5 mr-2" /> Elegir archivo (máx. 1 MB)
              </Button>
            </div>
            {busy && <p className="text-sm text-muted-foreground">Analizando el archivo...</p>}
          </div>
        )}

        {step === "preview" && preview && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {preview.source}. La vista previa vence en {Math.round(preview.expires_in_seconds / 60)} minutos. Los
              servidores importados nacen en <strong>solo lectura</strong>.
            </p>
            {preview.candidates.length === 0 && (
              <p data-testid="import-empty" className="text-sm text-muted-foreground">
                No se encontró ningún servidor SSH en este archivo.
              </p>
            )}
            {preview.candidates.map((c) => (
              <CandidateRow
                key={c.id}
                candidate={c}
                checked={!!checked[c.id]}
                name={names[c.id] || ""}
                onToggle={(value) => setChecked((prev) => ({ ...prev, [c.id]: value }))}
                onName={(value) => setNames((prev) => ({ ...prev, [c.id]: value }))}
              />
            ))}
            {preview.skipped.length > 0 && (
              <div data-testid="import-skipped" className="rounded-lg border border-dashed border-border p-3 space-y-1">
                <p className="text-xs font-medium">No se importarán (no son servidores SSH)</p>
                <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
                  {preview.skipped.map((s) => (
                    <li key={s.title}>
                      {s.title}
                      {s.reason !== "no es un servidor SSH" ? ` — ${s.reason}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {error && <p className="text-sm text-red-500">{error}</p>}
            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={reset} disabled={commit.isPending}>
                Elegir otro archivo
              </Button>
              <Button data-testid="import-commit-button" onClick={handleCommit} disabled={commit.isPending || selectedIds.length === 0}>
                {commit.isPending ? "Importando..." : `Importar seleccionados (${selectedIds.length})`}
              </Button>
            </div>
          </div>
        )}

        {step === "done" && results && (
          <div className="space-y-3">
            <ul className="space-y-1.5">
              {results.map((r) => (
                <li key={r.id} className={`text-sm ${r.status === "importado" ? "text-emerald-500" : "text-amber-600"}`}>
                  {r.status === "importado" ? "✅" : "⚠️"} {r.name}: {r.message}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              Importar no abre ninguna conexión. Prueba cada servidor con «Probar conexión», una vez por servidor (los
              intentos fallidos repetidos pueden bloquear la IP del panel).
            </p>
            <div className="flex justify-end">
              <Button onClick={() => handleOpenChange(false)}>Listo</Button>
            </div>
          </div>
        )}

        {step === "source" && error && <p className="text-sm text-red-500">{error}</p>}
      </DialogContent>
    </Dialog>
  );
}
